import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import * as Icons from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Statistic {
  id: string;
  title: string;
  value: number;
  suffix: string;
  description: string;
  icon_name: string;
  category: string;
  order: number;
}

const Counter: React.FC<{ stat: Statistic; start: boolean }> = ({ stat, start }) => {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!start || hasAnimated.current) return;

    hasAnimated.current = true;

    let current = 0;
    const duration = 800;
    const steps = 40;
    const increment = stat.value / steps;

    const interval = setInterval(() => {
      current += increment;
      if (current >= stat.value) {
        setCount(stat.value);
        clearInterval(interval);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(interval);
  }, [start, stat.value]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={start ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4 }}
      whileHover={{ y: -6 }}
      className="
        bg-[#0B0F19] 
        border border-white/10 
        rounded-2xl 
        p-8 
        transition-all duration-300 
        group
        hover:border-blue-500/20
      "
    >
      <div className="text-blue-400/70 mb-6">
        {React.createElement(
          Icons[stat.icon_name as keyof typeof Icons] || Icons.Star,
          { className: "w-7 h-7" }
        )}
      </div>

      <div className="text-5xl md:text-6xl font-semibold text-white mb-3">
        {count}
        <span className="text-2xl ml-1 text-gray-500">{stat.suffix}</span>
      </div>

      <h3 className="text-lg font-medium text-white mb-2">
        {stat.title}
      </h3>

      <p className="text-gray-500 text-sm">
        {stat.description}
      </p>
    </motion.div>
  );
};

export default function StatsSection() {
  const [stats, setStats] = useState<Statistic[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const { data, error } = await supabase
        .from('statistics')
        .select('*')
        .order('order', { ascending: true });

      if (error) throw error;
      setStats(data || []);
    } catch (error) {
      console.error(error);
      setStats([]);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'Tous les résultats' },
    { id: 'clients', label: 'Nos clients' },
    { id: 'performance', label: 'Performance' },
    { id: 'croissance', label: 'Croissance' },
  ];

  const filteredStats =
    selectedCategory === 'all'
      ? stats
      : stats.filter((s) => s.category === selectedCategory);

  return (
    <section ref={ref} className="py-24 bg-black">
      <div className="container mx-auto px-4">

        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-semibold mb-4 text-white">
            Des résultats mesurables
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-lg">
            Impact réel du marketing et de l’IA
          </p>
        </div>

        {/* FILTERS */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`
                px-6 py-2 rounded-full text-sm transition
                ${
                  selectedCategory === category.id
                    ? "bg-blue-500 text-white"
                    : "bg-[#0B0F19] text-gray-400 hover:bg-blue-500/20"
                }
              `}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* GRID */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-[#0B0F19] rounded-2xl p-8 animate-pulse h-64"></div>
            ))}
          </div>
        ) : (
          <div className="flex justify-center">
            <div
              className={`grid gap-6 w-full
                ${filteredStats.length === 1 ? "grid-cols-1 max-w-sm" : ""}
                ${filteredStats.length === 2 ? "grid-cols-1 md:grid-cols-2 max-w-2xl" : ""}
                ${filteredStats.length >= 3 ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4" : ""}
              `}
            >
              {filteredStats.map((stat) => (
                <Counter key={stat.id} stat={stat} start={isInView} />
              ))}
            </div>
          </div>
        )}

      </div>
    </section>
  );
}